// txn_interpreter_core.js
// Pure helpers for TxnHistoryInterpreter.vue
// No references to `this` or component state.

// External helpers you already have:
import Papa from 'papaparse';
import {
  firstHeaderMatch,
  looksLikeAddress,
  isTxHashLike,
  normalizeHeader,
  parseAmount,
} from '@/utils/interpreter_utils.js';

/* ================================
 * Tunable knobs (moved off component)
 * ================================ */
export const DEFAULT_KNOBS = {
  // Existing knobs
  MIN_TXHASH_SCORE: 2,
  MIN_ADDR_SCORE: 3,
  MIN_ADDR_RATIO: 0.04,

  MIN_AMOUNT_SCORE: 1.5,
  AMT_NUMERIC_WEIGHT: 5.0,
  AMT_USD_PENALTY: 6.0,
  AMT_NATIVE_PENALTY: 4.0,
  AMT_STRONG_AMOUNT_BONUS: 6.0,
  AMT_SIGN_DIVERSITY_WEIGHT: 4.0,
  AMT_TYPE_CORR_WEIGHT: 5.0,

  // NEW: hard content gates for hash and amount
  MIN_TXHASH_CONTENT_HITS: 3,         // require at least N rows with a real hash
  MIN_TXHASH_CONTENT_RATIO: 0.01,     // and at least 1% of sampled rows look like hashes

  // For amount when there is NO amount-like header:
  AMOUNT_MIN_NUMERIC_RATIO: 0.10,     // at least 10% of rows must parse as numeric
  AMOUNT_MIN_DECIMAL_FRAC_NOHEADER: 0.05, // at least 5% having decimals helps avoid IDs
  AMOUNT_MIN_SIGN_DIVERSITY_NOHEADER: 0.02, // some +/- diversity helps avoid IDs
  AMOUNT_MAX_IDLIKE_FRAC_NOHEADER: 0.70,    // if >70% are "ID-like" long ints, disqualify
  IDLIKE_INTEGER_MIN_DIGITS: 8,       // long-int threshold
};

/* ================================
 * Domain dictionaries
 * ================================ */
export function headerSets() {
  return {
    txHash: [
      'blockchain_txn_id','txid','transaction_hash','txn_hash','transaction id','transaction_id','hash','tx_hash',
      'hash_id','hashid'
    ],
    toAddress: [
      'to_address','destination','recipient','withdraw_address','address','to','to addr','toaddr'
    ],
    fromAddress: [ // NEW
      'from_address','from','source','source address','sender','from wallet','from account',
      'sender_address','origin','origin_address'
    ],
    transferType: [
      'transfer_type','type','direction','action','operation','transfer','category',
      'transaction_kind','kind','tx_type',
      'transaction_type',
      'method','method_name','txn_method','function','function_name'
    ],
    asset: ['asset','currency','currency_code','coin','symbol','token','ticker'],
    amount: [
      'amount','qty','quantity','size','value','net_amount','gross_amount','change','net','debit','credit',
      'to_amount','native_amount','native_amount_in_usd',
      'quantity_transacted'
    ],
    date: [
      'date','time','timestamp','created_at','createdat','datetime','time_utc','timestamp_utc',
    ],
       notes: ['notes','note','memo','description','narrative'],
    dHints: [
      'price_currency','price_at_transaction','subtotal',
      'total_inclusive_of_fees_and_or_spread','fees_and_or_spread','notes'
    ]
  };
}

export function knownFormats() {
  const H = headerSets();
  return [
    { label: 'StandardSetA',
      requiresAny: [...H.txHash, ...H.toAddress, ...H.transferType, ...H.asset, ...H.amount, ...H.date],
      strongHints: ['currency_code','native_network_fee','transfer_type','blockchain_txn_id']
    },
    { label: 'StandardSetB',
      requiresAny: [...H.txHash, ...H.toAddress, 'operation', 'fee', ...H.asset, ...H.date],
      strongHints: ['operation','address','txid']
    },
    { label: 'StandardSetC',
      requiresAny: ['transaction_hash','transaction_kind', ...H.amount, ...H.asset, 'timestamp_utc'],
      strongHints: ['native_amount','native_amount_in_usd','transaction_description','currency']
    },
    { label: 'StandardSetD',
      requiresAny: ['hash_id', 'transaction_type', 'asset', 'quantity_transacted', 'timestamp'],
      strongHints: (H.dHints || [])
    }
  ];
}

export function guessFormat(headerNorm) {
  const formats = knownFormats();
  let best = { label: 'Unknown/Custom', matched: 0, considered: headerNorm.length };
  for (const f of formats) {
    let match = 0;
    for (const h of headerNorm) if (f.requiresAny.includes(h)) match++;
    if (f.strongHints) for (const h of headerNorm) if (f.strongHints.includes(h)) match += 1;
    if (match > best.matched) best = { label: f.label, matched: match, considered: headerNorm.length };
  }
  return best;
}

/* ================================
 * Method/Transfer normalization
 * ================================ */
export function isMethodLikeHeader(headerNorm, idx) {
  if (idx == null || idx < 0) return false;
  const name = String(headerNorm?.[idx] || '').toLowerCase();
  return name === 'method' || name === 'method_name' || name === 'txn_method'
      || name === 'function' || name === 'function_name';
}

export function normalizeMethodCell(v) {
  const s = String(v ?? '')
    .replace(/\u00a0/g, ' ')
    .trim()
    .toLowerCase();

  if (/^\s*transfer\s*\(/i.test(s)) return 'transfer';

  const collapsed = s
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');

  return collapsed;
}

export function looksLikeTransferSignature(v) {
  return /^\s*transfer\s*\(/i.test(String(v ?? ''));
}

export function termSets() {
  const outTerms = new Set([
    'withdrawal','withdraw','sent','send','transfer_out','payout','out','debit','crypto_withdrawal'
  ]);
  const inTerms = new Set([
    'deposit','receive','received','transfer_in','credit','in','crypto_deposit'
  ]);
  return { outTerms, inTerms };
}
/* ================================
 * Notes variant helpers
 * ================================ */

// Robustly grab the first crypto address embedded in free text.
export function extractFirstAddressFromText(text) {
  const s = String(text || '');
  if (!s) return null;

  // ETH / EVM
  const eth = /0x[a-fA-F0-9]{40}/g;
  // BTC base58 P2PKH/P2SH
  const btc58 = /(?:^|[^A-Za-z0-9])([13][a-km-zA-HJ-NP-Z1-9]{25,34})(?:[^A-Za-z0-9]|$)/g;
  // BTC bech32 mainnet
  const btcBech = /bc1[0-9ac-hj-np-z]{11,71}/gi;
  // TRON base58
  const tron = /T[1-9A-HJ-NP-Za-km-z]{33}/g;

  const tests = [eth, btcBech, tron, btc58];
  for (const re of tests) {
    re.lastIndex = 0;
    const m = re.exec(s);
    if (m) return (m[1] || m[0]).trim();
  }
  return null;
}
// Detects Standard Set C:
// - transaction_hash
// - transaction_kind (e.g. "crypto_withdrawal")
// - amount
// - currency
// - timestamp_utc (or other date-ish fallback)
// - destination / to_address / withdraw_address (optional but used if present)
export function detectStandardSetC(headerRaw, headerNorm) {
  const H = (headerNorm || []).map(s => String(s || '').toLowerCase().trim());
  const T = H.map(headerTokens);

  const txHashIdx = findIdxHybrid(H, T, [
    /^transaction\s*hash$/, ['transaction','hash']
  ]);

  // This is the defining column for Set C
  const kindIdx = findIdxHybrid(H, T, [
    /^transaction_kind$/ // keep strict to avoid collisions
  ]);

  // Amount: prefer exact "amount", then try known variants used in your formats list
  const amountIdx = findIdxHybrid(H, T, [
    /^amount$/, ['native','amount'], ['gross','amount'], ['net','amount']
  ]);

  // Asset: prefer "currency", but allow common synonyms
  const assetIdx = findIdxHybrid(H, T, [
    /^currency$/, /^asset$/, ['currency','code'], ['coin'], ['token'], ['symbol']
  ]);

  // Destination (optional but very helpful)
  const toIdx = findIdxHybrid(H, T, [
    ['withdraw','address'], ['withdrawal','address'],
    ['to','address'], ['destination'], ['recipient'], /^address$/
  ]);

  // Time
  const timeIdx = findIdxHybrid(H, T, [
    /^timestamp_utc$/, /^timestamp$/, ['date','time'], ['datetime'], /^date$/
  ]);

  // Require the distinctive columns for Set C
  if (kindIdx < 0 || amountIdx < 0 || assetIdx < 0) return null;

  return {
    isCsvStandardSetC: true,
    columns: {
      txHash:       txHashIdx,  // may be -1 in some exports
      toAddress:    toIdx,      // may be -1; we will still include rows if the type matches
      transferType: kindIdx,    // "crypto_withdrawal" marks withdrawals
      amount:       amountIdx,
      asset:        assetIdx
    },
    timeCol: timeIdx
  };
}

// Quick detector: "Set D minus hash, with Notes"
export function looksLikeSetDNotes(headerNorm) {
  const H = headerSets();
  const hasType = headerNorm.includes('transaction_type');
  const hasAsset = headerNorm.includes('asset');
  const hasQty = headerNorm.includes('quantity_transacted');
  const hasTs = headerNorm.includes('timestamp') || headerNorm.includes('timestamp_utc');
  const hasNotes = firstHeaderMatch(headerNorm, H.notes) >= 0;
  const hasHashId = headerNorm.includes('hash_id') || headerNorm.includes('transaction_hash') || headerNorm.includes('tx_hash') || headerNorm.includes('txid');
  return hasType && hasAsset && hasQty && hasTs && hasNotes && !hasHashId;
}


/* ================================
 * CSV and grid normalization
 * ================================ */
export function sanitizeCell(value, opts) {
  const { trimWhitespace = true, neutralizeFormulas = true, MAX_CELL_CHARS = 10000 } = opts || {};
  let s = (value == null) ? '' : String(value);
  if (trimWhitespace) s = s.trim();
  if (s.length > MAX_CELL_CHARS) s = s.slice(0, MAX_CELL_CHARS);
  if (neutralizeFormulas && /^[=+@]/.test(s)) s = "'" + s;
  return s;
}
export function isLikelyBinary(str) {
  let controlCount = 0;
  for (let i = 0; i < str.length; i++) {
    const code = str.charCodeAt(i);
    const ok = code === 10 || code === 13 || code === 9;
    const control = (code < 32 && !ok) || code === 127;
    if (control) controlCount++;
    if (controlCount >= 4) return true;
  }
  return false;
}





// Split header into lowercase "word" tokens (letters+digits)

function headerTokens(s) {
  return (String(s || '')
    .toLowerCase()
    .match(/[a-z0-9]+/g)) || [];
}

// Return the first column index whose token-set includes *all* words in `need`.
// `need` is an array of words, e.g., ['amount','credited'].
function findIdxByWords(allTokenized, need) {
  for (let i = 0; i < allTokenized.length; i++) {
    const toks = allTokenized[i];
    let ok = true;
    for (const w of need) {
      if (!toks.includes(w)) { ok = false; break; }
    }
    if (ok) return i;
  }
  return -1;
}

// Return the first column index that matches ANY of the word-lists in `cands`.
// Each item in `cands` is either an array of words (all must be present),
// or a RegExp (checked against the *normalized* header string).
function findIdxHybrid(headersNorm, allTokenized, cands) {
  for (let i = 0; i < headersNorm.length; i++) {
    const norm = headersNorm[i];
    const toks = allTokenized[i];

    for (const cand of cands) {
      if (Array.isArray(cand)) {
        let ok = true;
        for (const w of cand) {
          if (!toks.includes(w)) { ok = false; break; }
        }
        if (ok) return i;
      } else if (cand instanceof RegExp) {
        if (cand.test(norm)) return i;
      }
    }
  }
  return -1;
}
// Detects Standard Set D (with optional HASH ID):
// Columns (normalized):
// - transaction_type (Send => withdrawal)
// - asset
// - quantity_transacted
// - timestamp or timestamp_utc
// - notes (used to extract destination address)
// - hash_id (optional) or transaction_hash / txid / tx_hash
export function detectStandardSetD(headerRaw, headerNorm) {
  const HN = (headerNorm || []).map(s => String(s || '').toLowerCase().trim());

  const idx = (names) => {
    for (const name of names) {
      const i = HN.indexOf(name);
      if (i >= 0) return i;
    }
    return -1;
  };

  const txTypeIdx = idx(['transaction_type']);
  const assetIdx  = idx(['asset']);
  const qtyIdx    = idx(['quantity_transacted']);
  const tsIdx     = idx(['timestamp','timestamp_utc']);
  const notesIdx  = idx(['notes']);

  // Hash can be missing in Set D
  let hashIdx = idx(['hash_id','transaction_hash','tx_hash','txid','txn_hash']);

  // Must have the defining columns
  if (txTypeIdx < 0 || assetIdx < 0 || qtyIdx < 0 || tsIdx < 0) {
    return null;
  }

  return {
    isCsvStandardSetD: true,
    columns: {
      txHash:       hashIdx,     // may be -1
      toAddress:    -1,          // destination will come from Notes extraction
      transferType: txTypeIdx,   // must equal "Send" for withdrawals
      asset:        assetIdx,
      amount:       qtyIdx
    },
    timeCol:  tsIdx,
    notesCol: notesIdx           // used later for Notes-mode address extraction
  };
}
// in txn_interpreter_core.js
export function detectDebitCreditCashoutCsvV1(headerRaw, headerNorm) {
  const H = (headerNorm || []).map(s => String(s || '').toLowerCase().trim());
  const T = H.map(headerTokens);

  const col = {
    type:     findIdxByWords(T, ['transaction','type']),
    date:     findIdxHybrid(H, T, [
      ['date','time'], ['date'], /\bdate\b.*\btime\b/
    ]),
    amtCred:  findIdxHybrid(H, T, [
      ['amount','credited'], ['amount','credit'],
      ['amt','credited'],    ['amt','credit']
    ]),
    credCurr: findIdxHybrid(H, T, [
      ['credit','currency'], ['credit','coin'], ['credit','asset'], ['credit','curr'],
      ['credited','currency'], ['currency','credited']
    ]),
    amtDeb:   findIdxHybrid(H, T, [
      ['amount','debited'], ['amount','debit'],
      ['amt','debited'],    ['amt','debit']
    ]),
    debCurr:  findIdxHybrid(H, T, [
      ['debit','currency'], ['debit','coin'], ['debit','asset'], ['debit','curr'],
      ['debited','currency'], ['currency','debited']
    ]),
    srcdst:   findIdxHybrid(H, T, [
      ['source','destination'],
      ['destination'],
      ['to','address'],
      ['to','wallet','address']
    ]),
    txid:     findIdxHybrid(H, T, [
      ['blockchain','transaction','id'],
      /^txid$/, ['tx','id'],
      ['transaction','hash'], ['tx','hash'],
      /^hash$/, ['txn','id']
    ]),
    direction: findIdxHybrid(H, T, [/^direction$/, /^flow$/, /^side$/]),
  };

  const hasCore     = (col.txid >= 0) && (col.srcdst >= 0) && (col.type >= 0);
  const hasCredPair = (col.amtCred >= 0) && (col.credCurr >= 0);
  const hasDebPair  = (col.amtDeb  >= 0) && (col.debCurr  >= 0);

  if (!hasCore || (!hasCredPair && !hasDebPair)) return null;

  const useCredit = hasCredPair;

  return {
    isCsvDebitCreditCashoutV1: true,
    columns: {
      txHash:       col.txid,
      toAddress:    col.srcdst,
      transferType: col.type,
      amount:       useCredit ? col.amtDeb: col.amtCred,  
      asset:        useCredit ? col.debCurr : col.credCurr,
    },
    timeCol:      col.date,
    directionCol: col.direction,
  };
}


// Detects CSVs with headers like:
// A: Transaction ID (internal, not a hash)
// B: Date
// C: Activity  -> "withdrawal-crypto" marks withdrawals
// D: Amount    -> on-chain amount
// E: Currency  -> token/asset symbol
// F: CAD Amount (ignore)
// G: CAD Rate  (ignore)
// H: Status    (ignore)
// I: Details   -> contains destination address (embedded text)
export function detectActivityDetailsCsvV1(headerRaw, headerNorm) {
  const H = (headerNorm || []).map(s => String(s || '').toLowerCase().trim());
  const T = H.map(headerTokens); // assumes you already have headerTokens()

  // Prefer exact/near-exact header names to avoid catching "CAD Amount".
  const amountIdx = findIdxHybrid(H, T, [/^amount$/]); // strict "Amount"
  const assetIdx  = findIdxHybrid(H, T, [/^currency$/]); // strict "Currency"

  const col = {
    // Optional internal id (not used for tx hash)
    internalId: findIdxHybrid(H, T, [
      ['transaction','id'], /^transaction\s*id$/
    ]),

    date:        findIdxHybrid(H, T, [['date'], /\bdate\b.*\btime\b/]),
    activity:    findIdxHybrid(H, T, [/^activity$/]),
    amount:      amountIdx,
    currency:    assetIdx,
    details:     findIdxHybrid(H, T, [/^details?$/]),

    // There is no blockchain hash column in this format
    txid:        -1
  };

  // Core requirements: Activity + Amount + Currency + Details present
  const hasCore =
    (col.activity >= 0) &&
    (col.amount   >= 0) &&
    (col.currency >= 0) &&
    (col.details  >= 0);

  if (!hasCore) return null;

  return {
    isCsvActivityDetailsV1: true,
    columns: {
      txHash:       col.txid,        // absent
      toAddress:    col.details,     // extract from embedded text
      transferType: col.activity,    // "withdrawal-crypto"
      amount:       col.amount,      // on-chain amount
      asset:        col.currency     // token symbol
    },
    timeCol: col.date                // Date
  };
}















export function maybeExplodeSingleCellCsv(rows) {
  try {
    if (!Array.isArray(rows) || !rows.length) return rows;
    const firstRow = rows[0] || [];
    if (rows.length === 1 && firstRow.length === 1) {
      const cell = firstRow[0];
      if (typeof cell === 'string' && /[\n\r]|,|\t|;/.test(cell)) {
        const parsed = Papa.parse(cell, {
          header: false,
          dynamicTyping: false,
          skipEmptyLines: 'greedy'
        });
        if (parsed && Array.isArray(parsed.data) && parsed.data.length) {
          return parsed.data;
        }
      }
    }
  } catch (_) { console.log("eslint say no Empty block statements"); }
  return rows;
}
export function detectBareTwoCols(rows, probeRows = 1000) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return { txHash: -1, toAddress: -1 };
  }
  const maxR = Math.min(rows.length, probeRows);
  const colCount = rows.reduce((m, r) => Math.max(m, (r || []).length), 0);
  if (colCount === 0) return { txHash: -1, toAddress: -1 };

  const txScore = new Array(colCount).fill(0);
  const addrScore = new Array(colCount).fill(0);

  for (let r = 0; r < maxR; r++) {
    const row = rows[r] || [];
    for (let c = 0; c < colCount; c++) {
      const v = String(row[c] ?? '').trim();
      if (!v) continue;
      if (isTxHashLike(v))      txScore[c]  += 1;
      if (looksLikeAddress(v))  addrScore[c] += 1;
    }
  }

  const argMax = (arr) => {
    let best = -1, bestVal = -Infinity;
    for (let i = 0; i < arr.length; i++) {
      if (arr[i] > bestVal) { bestVal = arr[i]; best = i; }
    }
    return best;
  };

  const txIdx = argMax(txScore);
  if (txIdx < 0 || txScore[txIdx] <= 0) return { txHash: -1, toAddress: -1 };

  let addrIdx = argMax(addrScore);
  if (addrIdx === txIdx) {
    const saved = addrScore[addrIdx];
    addrScore[addrIdx] = -Infinity;
    const second = argMax(addrScore);
    addrScore[addrIdx] = saved;
    addrIdx = second;
  }

  if (addrIdx < 0 || addrScore[addrIdx] <= 0) addrIdx = -1;
  return { txHash: txIdx, toAddress: addrIdx };
}

/* ================================
 * Header detection and column picking
 * ================================ */
export function findHeaderRow(allRows, probe = 25) {
  const H = headerSets();
  const needles = new Set([].concat(
    H.txHash, H.toAddress, H.transferType, H.asset, H.amount, H.date, (H.dHints || []), (H.notes || [])
  ));

  let best = { score: -1, idx: 0, head: [] };
  const limit = Math.min(allRows.length, probe);

  for (let r = 0; r < limit; r++) {
    const row = allRows[r] || [];
    let score = 0;
    const rowNorm = row.map(normalizeHeader);

    for (const n of rowNorm) {
      if (!n) continue;
      if (needles.has(n)) score += 2;
      if (n.includes('tx') || n.includes('hash')) score += 1;
      if (n.includes('address') || n === 'to') score += 1;
      if (n.includes('amount') || n === 'currency') score += 1;
      if (n === 'notes' || n === 'memo' || n === 'description') score += 1;
    }

    const hasHashId   = rowNorm.includes('hash_id');
    const hasTxType   = rowNorm.includes('transaction_type');
    const hasAsset    = rowNorm.includes('asset');
    const hasQty      = rowNorm.includes('quantity_transacted');
    const hasTime     = rowNorm.includes('timestamp') || rowNorm.includes('timestamp_utc');

    const dCount = [hasHashId, hasTxType, hasAsset, hasQty].filter(Boolean).length;
    if (dCount >= 2) score += 3 * dCount;
    if (dCount >= 3) score += 5;
    if (dCount === 4 && hasTime) score += 4;

    if (score > best.score) best = { score, idx: r, head: row.slice() };
  }

  const hasHeader = best.score >= 2;
  const header = (hasHeader ? best.head : []);
  const headerNorm = header.map(normalizeHeader);
  return { rowIndex: best.idx, header: headerNorm, hasHeader };
}

export function detectColumns(rows, headerRowIdx, headerNorm, withStats = false, knobs = DEFAULT_KNOBS) {
  const H = headerSets();
  const sampleStart = headerRowIdx + 1;
  const sampleEnd = Math.min(rows.length, sampleStart + 500);
  const colCount = rows[headerRowIdx]?.length || 0;

  let transferTypeCol = firstHeaderMatch(headerNorm, H.transferType);

  const isMethodLikeName = (s) => {
    const n = String(s || '').toLowerCase();
    return n === 'method' || n === 'method_name' || n === 'txn_method'
        || n === 'function' || n === 'function_name';
  };
  const transferHeaderIsMethod =
    (transferTypeCol >= 0) && isMethodLikeName(headerNorm[transferTypeCol]);

  const scores = {
    txHash: new Array(colCount).fill(0),
    toAddress: new Array(colCount).fill(0),

    amtHeaderBase: new Array(colCount).fill(0),
    amtNumericCount: new Array(colCount).fill(0),
    amtPosCount: new Array(colCount).fill(0),
    amtNegCount: new Array(colCount).fill(0),
    amtCorrGood: new Array(colCount).fill(0),
    amtCorrBad: new Array(colCount).fill(0),

    txContentHits: new Array(colCount).fill(0),
    amtDecimalCount: new Array(colCount).fill(0),
    amtIdLikeCount: new Array(colCount).fill(0),

    samples: 0
  };

  // header hints and penalties
  for (let c = 0; c < colCount; c++) {
    const h = headerNorm[c] || '';

    if (H.txHash.includes(h))   scores.txHash[c]   += 5;
    if (H.toAddress.includes(h)) scores.toAddress[c] += 5;

    if (H.amount.includes(h)) {
      scores.amtHeaderBase[c] += 3;
      if (h === 'amount') scores.amtHeaderBase[c] += knobs.AMT_STRONG_AMOUNT_BONUS;
      if (h === 'quantity_transacted') scores.amtHeaderBase[c] += (knobs.AMT_STRONG_AMOUNT_BONUS + 4);
    }
    if (h.includes('usd'))   scores.amtHeaderBase[c] -= knobs.AMT_USD_PENALTY;
    if (h.includes('native')) scores.amtHeaderBase[c] -= knobs.AMT_NATIVE_PENALTY;
  }

  const { outTerms, inTerms } = termSets();

  for (let r = sampleStart; r < sampleEnd; r++) {
    const row = rows[r] || [];

    for (let c = 0; c < colCount; c++) {
      const v = String(row[c] ?? '').trim();
      if (!v) continue;
      if (isTxHashLike(v)) { scores.txHash[c] += 1; scores.txContentHits[c] += 1; }
      if (looksLikeAddress(v)) scores.toAddress[c] += 1;
    }

    let typeVal = '';
    if (transferTypeCol >= 0) typeVal = String(row[transferTypeCol] || '').trim().toLowerCase();

    for (let c = 0; c < colCount; c++) {
      const raw = String(row[c] ?? '').trim();
      const parsed = parseAmount(raw);
      if (!parsed) continue;

      scores.amtNumericCount[c] += 1;

      const num = parsed.num;
      if (num > 0) scores.amtPosCount[c] += 1;
      else if (num < 0) scores.amtNegCount[c] += 1;

      if (/[.,]/.test(raw)) scores.amtDecimalCount[c] += 1;
      if (/^\d+$/.test(raw) && raw.length >= knobs.IDLIKE_INTEGER_MIN_DIGITS) {
        scores.amtIdLikeCount[c] += 1;
      }

      if (transferTypeCol >= 0 && !transferHeaderIsMethod) {
        if (outTerms.has(typeVal)) {
          if (num < 0) scores.amtCorrGood[c] += 1; else if (num > 0) scores.amtCorrBad[c] += 1;
        } else if (inTerms.has(typeVal)) {
          if (num > 0) scores.amtCorrGood[c] += 1; else if (num < 0) scores.amtCorrBad[c] += 1;
        }
      }
    }

    scores.samples++;
  }

  // amount scoring
  const amtFinal = new Array(colCount).fill(Number.NEGATIVE_INFINITY);
  for (let c = 0; c < colCount; c++) {
    const numericRatio = scores.samples > 0 ? (scores.amtNumericCount[c] / scores.samples) : 0;
    const headerPart   = scores.amtHeaderBase[c];
    const pos = scores.amtPosCount[c], neg = scores.amtNegCount[c];
    const sdiv = (pos > 0 && neg > 0) ? (Math.min(pos, neg) / Math.max(1, pos + neg)) : 0;
    const corrTot = scores.amtCorrGood[c] + scores.amtCorrBad[c];
    const corrScore = corrTot > 0 ? ((scores.amtCorrGood[c] - scores.amtCorrBad[c]) / corrTot) : 0;

    amtFinal[c] = headerPart
      + numericRatio * DEFAULT_KNOBS.AMT_NUMERIC_WEIGHT
      + sdiv * DEFAULT_KNOBS.AMT_SIGN_DIVERSITY_WEIGHT
      + corrScore * DEFAULT_KNOBS.AMT_TYPE_CORR_WEIGHT;
  }

  // HARD GATES: TX HASH (adaptive)
  const total = Math.max(1, scores.samples);
  for (let c = 0; c < colCount; c++) {
    const hits = scores.txContentHits[c];
    const ratio = hits / total;

    let minHits  = DEFAULT_KNOBS.MIN_TXHASH_CONTENT_HITS;
    let minRatio = DEFAULT_KNOBS.MIN_TXHASH_CONTENT_RATIO;

    if (total <= 10) { minHits = 1; minRatio = 0; }
    else if (total <= 50) { minHits = Math.min(minHits, 2); }

    const h = (headerNorm[c] || '');
    const strongHeader = ['transaction_hash','txid','txn_hash','hash','hash_id','tx_hash','blockchain_txn_id'].includes(h);
    if (strongHeader) minHits = Math.max(1, minHits - 1);

    if (hits < minHits || ratio < minRatio) {
      scores.txHash[c] = Number.NEGATIVE_INFINITY;
    }
  }

  const pickWithFloor = (arr, floor) => {
    let best = { val: Number.NEGATIVE_INFINITY, idx: -1 };
    for (let i = 0; i < arr.length; i++) if (arr[i] > best.val) best = { val: arr[i], idx: i };
    return (best.val >= floor) ? best.idx : -1;
  };

  let txHashColCand = pickWithFloor(scores.txHash, DEFAULT_KNOBS.MIN_TXHASH_SCORE);

  let toAddressColCand = -1;
  const toHeaderHit = firstHeaderMatch(headerNorm, H.toAddress);
  if (toHeaderHit >= 0) {
    toAddressColCand = toHeaderHit;
  } else {
    const bestAddr = scores.toAddress.reduce((acc, v, i) => (v > acc.val ? { val: v, idx: i } : acc), { val: -1, idx: -1 });
    const sufficientHits = bestAddr.val >= DEFAULT_KNOBS.MIN_ADDR_SCORE;
    const sufficientRatio = (scores.samples > 0) && ((bestAddr.val / scores.samples) >= DEFAULT_KNOBS.MIN_ADDR_RATIO);
    toAddressColCand = (sufficientHits || sufficientRatio) ? bestAddr.idx : -1;
  }

  const assetCol = firstHeaderMatch(headerNorm, H.asset);

  const forbidden = new Set();
  if (txHashColCand >= 0) forbidden.add(txHashColCand);
  if (toAddressColCand >= 0) forbidden.add(toAddressColCand);

  let amountCol = -1;
  let bestVal = Number.NEGATIVE_INFINITY;
  for (let c = 0; c < colCount; c++) {
    if (forbidden.has(c)) continue;
    if (amtFinal[c] > bestVal) { bestVal = amtFinal[c]; amountCol = c; }
  }
  if (bestVal < DEFAULT_KNOBS.MIN_AMOUNT_SCORE) amountCol = -1;

  if (amountCol >= 0) {
    const h = (headerNorm[amountCol] || '').toLowerCase();
    const headerLooksAmount =
      H.amount.includes(h) || /amount|value|qty|quantity|size|net|gross|debit|credit/.test(h);

    if (!headerLooksAmount) {
      const numeric = scores.amtNumericCount[amountCol];
      const decimals = scores.amtDecimalCount[amountCol];
      const idlike = scores.amtIdLikeCount[amountCol];
      const pos = scores.amtPosCount[amountCol], neg = scores.amtNegCount[amountCol];
      const sdiv = (pos > 0 && neg > 0) ? (Math.min(pos, neg) / Math.max(1, pos + neg)) : 0;

      const numericRatio = scores.samples > 0 ? (numeric / scores.samples) : 0;
      const decFrac = numeric > 0 ? (decimals / numeric) : 0;
      const idFrac  = numeric > 0 ? (idlike  / numeric) : 0;

      const failsNumeric   = numericRatio < DEFAULT_KNOBS.AMOUNT_MIN_NUMERIC_RATIO;
      const lacksDecimals  = decFrac < DEFAULT_KNOBS.AMOUNT_MIN_DECIMAL_FRAC_NOHEADER;
      const lacksSigns     = sdiv < DEFAULT_KNOBS.AMOUNT_MIN_SIGN_DIVERSITY_NOHEADER;
      const tooIdLike      = idFrac > DEFAULT_KNOBS.AMOUNT_MAX_IDLIKE_FRAC_NOHEADER;

      if (failsNumeric || ((lacksDecimals && lacksSigns) && tooIdLike)) {
        amountCol = -1;
      }
    }
  }

  const out = {
    txHash: txHashColCand,
    toAddress: toAddressColCand,
    transferType: transferTypeCol,
    asset: assetCol,
    amount: amountCol
  };

  if (withStats) {
    out._stats = {
      txHashScore: scores.txHash,
      addrScore: scores.toAddress,
      amountScore: amtFinal,
      txHits: scores.txContentHits,
      amtNumeric: scores.amtNumericCount,
      amtDecimals: scores.amtDecimalCount,
      amtIdLike: scores.amtIdLikeCount,
      samples: scores.samples
    };
  }
  return out;
}

/* ================================
 * Misc small helpers
 * ================================ */
export function isNumericLike(value) {
  const parsed = parseAmount(value);
  return !!parsed;
}
